import { useQuery } from "@apollo/client";
import { Search } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  InputAdornment,
  Typography,
  styled,
} from "@mui/material";
import _ from "lodash";
import { useSnackbar } from "notistack";
import { ReactElement, useCallback, useMemo, useState } from "react";

import {
  ImportResponseScriptsMutation,
  ImportResponseScriptsMutationVariables,
  ListResponseScriptsQuery,
  ListResponseScriptsQueryVariables,
} from "~frontend/__generated__/graphql";
import { Checkbox } from "~frontend/components/Checkbox";
import { FlexBox } from "~frontend/components/FlexBox";
import Loading from "~frontend/components/Loading";
import TagChip from "~frontend/components/TagChip";
import FormTextField from "~frontend/components/forms/FormTextField";
import {
  MediaFieldValue,
  MediaPreview,
} from "~frontend/components/forms/FormikMediaField";
import ChevronDown from "~frontend/components/icons/ChevronDown";
import ChevronUp from "~frontend/components/icons/ChevronUp";
import {
  ScriptContent,
  ScriptGroup,
  ScriptGroupHeading,
  ScriptKeywords,
  ScriptResponse,
  ScriptResponseScript,
  ScriptResponseTitle,
  ScriptResponses,
  ScriptTags,
} from "~frontend/components/responseScripts/styles";
import {
  IMPORT_RESPONSE_SCRIPTS,
  LIST_RESPONSE_SCRIPT_GROUPS,
} from "~frontend/graphql/scripts";
import { useSubaccount } from "~frontend/providers/SubaccountProvider";
import theme, { palette } from "~frontend/theme";
import { useErrorMessageFormatter } from "~frontend/util/errors";
import useDebounce from "~frontend/util/useDebounce";
import useMutation from "~frontend/util/useMutation";

import { CampaignForEditor } from "../types";

type ResponseScriptGroup =
  ListResponseScriptsQuery["listResponseScripts"][number];
type ResponseScript = ResponseScriptGroup["memberships"][number];

const CampaignScriptGroupWrapper = styled("div")(({ theme }) => ({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  padding: theme.spacing(2),
  paddingBottom: theme.spacing(1),
  marginBottom: theme.spacing(2),
}));

const CampaignScriptHeadingAndCheckbox = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  height: theme.spacing(2),
  "& svg": {
    transform: "scale(0.875)",
  },
  gap: theme.spacing(1),
  marginLeft: -12, // Adjustment for ripple around checkbox
}));

export function ScriptImporter({
  campaign,
  onImport,
}: {
  campaign: CampaignForEditor;
  onImport: () => void;
}): ReactElement {
  const { subaccount } = useSubaccount()!;
  const snackbar = useSnackbar();
  const getErrorMessage = useErrorMessageFormatter();

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 500);

  const groupsResult = useQuery<
    ListResponseScriptsQuery,
    ListResponseScriptsQueryVariables
  >(LIST_RESPONSE_SCRIPT_GROUPS, {
    variables: { input: { subaccountId: subaccount.id } },
    pollInterval: 60 * 1000,
  });

  const responseScriptGroups = groupsResult.data?.listResponseScripts;

  const filteredResponseScriptGroups = useMemo(() => {
    return responseScriptGroups?.flatMap(group => {
      if (!group.memberships.length) {
        return [];
      } else if (
        group.title.toLowerCase().includes(debouncedSearch.toLowerCase())
      ) {
        return [group];
      } else {
        const filteredMemberships: ResponseScript[] = [];
        for (const membership of group.memberships) {
          if (
            membership.script.title
              .toLowerCase()
              .includes(debouncedSearch.toLowerCase()) ||
            membership.script.script
              .toLowerCase()
              .includes(debouncedSearch.toLowerCase()) ||
            membership.script.keywords.some(
              keyword =>
                keyword.normalizedKeyword
                  .toLowerCase()
                  .includes(debouncedSearch.toLowerCase()) ||
                membership.script.tags.some(tag =>
                  tag.name.toLowerCase().includes(debouncedSearch.toLowerCase())
                )
            )
          ) {
            filteredMemberships.push(membership);
          }
        }
        if (filteredMemberships.length) {
          return [{ ...group, memberships: filteredMemberships }];
        } else {
          return [];
        }
      }
    });
  }, [responseScriptGroups, debouncedSearch]);

  const [groupOpen, setGroupOpen] = useState({} as Record<string, boolean>);

  const [selectedMembershipIds, setSelectedMembershipIds] = useState(
    new Set<string>()
  );

  const selectMembershipIds = useCallback(
    (membershipIds: string[]) =>
      setSelectedMembershipIds(
        new Set([...selectedMembershipIds, ...membershipIds])
      ),
    [setSelectedMembershipIds, selectedMembershipIds]
  );
  const unselectMembershipIds = useCallback(
    (membershipIds: string[]) =>
      setSelectedMembershipIds(
        new Set(
          [...selectedMembershipIds].filter(
            membershipId => !membershipIds.includes(membershipId)
          )
        )
      ),
    [setSelectedMembershipIds, selectedMembershipIds]
  );

  const selectedGroupIds = useMemo(() => {
    if (!responseScriptGroups) {
      return new Set<string>();
    }

    return new Set(
      responseScriptGroups
        .filter(group =>
          group.memberships.every(membership =>
            selectedMembershipIds.has(membership.id)
          )
        )
        .map(group => group.id)
    );
  }, [responseScriptGroups, selectedMembershipIds]);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const [importScriptGroups] = useMutation<
    ImportResponseScriptsMutation,
    ImportResponseScriptsMutationVariables
  >(IMPORT_RESPONSE_SCRIPTS, {
    refetchQueries: ["getCampaign"],
    awaitRefetchQueries: true,
  });

  const handleImport = useCallback(
    async (membershipIds: string[]) => {
      setIsSubmitting(true);

      const { errors } = await importScriptGroups({
        variables: {
          input: {
            campaignId: campaign.id,
            membershipIds,
          },
        },
      });

      setIsSubmitting(false);

      if (errors) {
        setSubmitError(getErrorMessage(errors));
      } else {
        setSubmitError(null);
        setSelectedMembershipIds(new Set());
        setGroupOpen({});
        onImport();
        snackbar.enqueueSnackbar(`Response scripts imported`);
      }
    },
    [
      setIsSubmitting,
      importScriptGroups,
      campaign,
      setSubmitError,
      getErrorMessage,
      setSelectedMembershipIds,
      setGroupOpen,
      onImport,
      snackbar,
    ]
  );

  const handleImportSelected = useCallback(async () => {
    await handleImport([...selectedMembershipIds]);
  }, [handleImport, selectedMembershipIds]);

  const handleImportAll = useCallback(async () => {
    if (!responseScriptGroups) {
      return;
    }

    await handleImport(
      responseScriptGroups
        .flatMap(group => group.memberships)
        .map(membership => membership.id)
    );
  }, [responseScriptGroups, handleImport]);

  return (
    <>
      {submitError && (
        <Alert severity="error" sx={{ marginBottom: 3 }}>
          {submitError}
        </Alert>
      )}
      {groupsResult.loading ? (
        <Loading />
      ) : (
        <>
          <FormTextField
            fullWidth
            placeholder="Search for a response or group"
            value={search}
            onChange={event => setSearch(event.target.value)}
            sx={{ paddingTop: 0 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
          <CampaignScriptGroupWrapper sx={{ padding: 0 }}>
            {filteredResponseScriptGroups?.length === 0 &&
              "No response scripts found."}
            {filteredResponseScriptGroups?.map(group => (
              <ScriptGroup
                key={group.id}
                sx={{
                  backgroundColor: selectedGroupIds.has(group.id)
                    ? palette.lightGray
                    : palette.lightestGray,
                }}
              >
                <ScriptGroupHeading
                  sx={{
                    cursor: "pointer",
                    alignItems: "flex-start",
                    height: "fit-content",
                    padding: "8px",
                  }}
                  onClick={() =>
                    setGroupOpen({
                      ...groupOpen,
                      [group.id]: !groupOpen[group.id],
                    })
                  }
                >
                  <CampaignScriptHeadingAndCheckbox
                    sx={{ height: "fit-content" }}
                  >
                    <Checkbox
                      checked={selectedGroupIds.has(group.id)}
                      onClick={e => {
                        e.stopPropagation();
                      }}
                      onChange={(_evt, checked) => {
                        const allGroupMembershipIds =
                          responseScriptGroups
                            ?.find(g => g.id === group.id)
                            ?.memberships.map(m => m.id) ?? [];
                        if (checked) {
                          selectMembershipIds(allGroupMembershipIds);
                        } else {
                          unselectMembershipIds(allGroupMembershipIds);
                        }
                      }}
                    />
                    <Box>
                      <Typography
                        variant="body2Bold"
                        sx={{
                          cursor: "pointer",
                          fontWeight: "bold",
                        }}
                      >
                        {group.title}
                      </Typography>
                      {groupOpen[group.id] ? (
                        <FlexBox gap="4px">
                          <Typography variant="body3Bold">
                            Hide Responses
                          </Typography>
                          <ChevronUp sx={{ width: "10px" }} />
                        </FlexBox>
                      ) : (
                        <FlexBox gap="4px">
                          <Typography variant="body3">
                            Show Responses
                          </Typography>
                          <ChevronDown sx={{ width: "10px" }} />
                        </FlexBox>
                      )}
                    </Box>
                  </CampaignScriptHeadingAndCheckbox>
                </ScriptGroupHeading>
                {groupOpen[group.id] && !!group.memberships.length && (
                  <ScriptResponses sx={{ paddingLeft: 0 }}>
                    {group.memberships.map(membership => (
                      <ScriptResponse
                        key={membership.id}
                        sx={{
                          paddingLeft: theme.spacing(5),
                          backgroundColor: selectedMembershipIds.has(
                            membership.id
                          )
                            ? palette.lightGray
                            : palette.lightestGray,
                        }}
                      >
                        <ScriptContent>
                          <ScriptResponseTitle>
                            <CampaignScriptHeadingAndCheckbox
                              sx={{ height: "fit-content" }}
                            >
                              <Checkbox
                                checked={selectedMembershipIds.has(
                                  membership.id
                                )}
                                onChange={(_evt, checked) => {
                                  if (checked) {
                                    selectMembershipIds([membership.id]);
                                  } else {
                                    unselectMembershipIds([membership.id]);
                                  }
                                }}
                              />
                              <Typography variant="body2Bold">
                                {membership.script.title}
                              </Typography>
                            </CampaignScriptHeadingAndCheckbox>
                          </ScriptResponseTitle>
                          <ScriptResponseScript>
                            <MediaPreview
                              media={
                                {
                                  upload: membership.script.mediaUpload,
                                } as MediaFieldValue
                              }
                            />
                            <Typography variant="body2">
                              {membership.script.script}
                            </Typography>

                            {membership.script.tags.length ? (
                              <ScriptTags>
                                {membership.script.tags.map(tag => (
                                  <TagChip key={tag.id} label={tag.name} />
                                ))}
                              </ScriptTags>
                            ) : null}

                            <ScriptKeywords
                              keywords={membership.script.keywords}
                            />
                          </ScriptResponseScript>
                        </ScriptContent>
                      </ScriptResponse>
                    ))}
                  </ScriptResponses>
                )}
              </ScriptGroup>
            ))}
          </CampaignScriptGroupWrapper>
        </>
      )}
      <FlexBox justifyContent="flex-end" gap="10px">
        <Button
          disabled={isSubmitting}
          color="tertiary"
          variant="contained"
          sx={{ height: "27px" }}
          onClick={handleImportAll}
        >
          <Typography variant="h4Caps">
            {isSubmitting ? "Importing" : "Import All Responses"}
          </Typography>
        </Button>
        <Button
          disabled={isSubmitting || !selectedMembershipIds.size}
          color="primary"
          variant="contained"
          sx={{ height: "27px" }}
          onClick={handleImportSelected}
        >
          <Typography variant="h4Caps">
            {isSubmitting ? "Importing" : "Import Selected"}
          </Typography>
        </Button>
      </FlexBox>
    </>
  );
}
